import * as React from "react"
import { Head, Link, router } from "@inertiajs/react"
import { marked } from "marked"
import DOMPurify from "isomorphic-dompurify"
import {
  ArrowLeft,
  CheckCircle2,
  Clock,
  Download,
  FilePlus2,
  FileSpreadsheet,
  FileText,
  Loader2,
  RefreshCw,
  ShieldAlert,
  XCircle,
} from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Alert } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Select } from "@/components/ui/select"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type RunStatus = "pending" | "processing" | "completed" | "failed" | "cancelled"

type RunDetail = {
  id: number
  source_folder: string
  subpath: string | null
  status: RunStatus
  file_count: number
  total_bytes: number
  source_mapped: boolean | null
  meta_files_written: number | null
  new_template_count: number
  integrity_issue_count: number
  ingest_ok_count: number | null
  error_message: string | null
  generated_at: string | null
  created_at: string
  uploaded_by: string | null
  stdout_tail: string | null
  xray_summary: Record<string, unknown> | null
}

type XrayFile = {
  id: number
  relative_path: string
  original_filename: string
  byte_size: number
  ext: string | null
  template_status: string | null
  fingerprint: string | null
  ingest_ok: boolean | null
  integrity_error: string | null
}

type Leaf = {
  leaf: string
  label: string
  xray_md_available: boolean
  extract_json_available: boolean
}

type Props = {
  run: RunDetail
  files: XrayFile[]
  leaves: Leaf[]
  selected_leaf: string | null
  xray_markdown: string | null
  xray_markdown_truncated: boolean
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function Show({
  run,
  files,
  leaves,
  selected_leaf,
  xray_markdown,
  xray_markdown_truncated,
}: Props) {
  const note = (run.xray_summary?.note as string | undefined) ?? null
  const generationRan = run.status === "completed" && !note
  const showUnmappedHint = generationRan && run.source_mapped === false

  function downloadUrl(leaf: string, artifact: "xray_md" | "extract_json") {
    const params = new URLSearchParams({ artifact, leaf })
    return `/admin/data-xray/${run.id}/download?${params.toString()}`
  }

  function switchLeaf(leaf: string) {
    router.get(
      `/admin/data-xray/${run.id}`,
      { leaf },
      {
        only: ["xray_markdown", "xray_markdown_truncated", "selected_leaf"],
        preserveState: true,
        preserveScroll: true,
        replace: true,
      },
    )
  }

  const selectedLeafEntry = leaves.find((l) => l.leaf === selected_leaf)
  const title = run.subpath ? `${run.source_folder} / ${run.subpath}` : run.source_folder

  return (
    <AppShell width="wide">
      <Head title={`Run X-Ray: ${run.source_folder}`}>
        <meta
          name="description"
          content={`Hasil x-ray untuk batch upload ${run.source_folder} — file meta yang dihasilkan, status template/ingest per file, dan dokumen _FOLDER_XRAY.md.`}
        />
        <meta property="og:title" content={`Run X-Ray: ${run.source_folder}`} />
        <meta
          property="og:description"
          content={`Detail run x-ray untuk ${run.source_folder}: meta yang ditulis, template baru, isu integritas, dan ingest_ok.`}
        />
      </Head>

      <div className="mb-4">
        <Link
          href="/admin/data-xray"
          className="inline-flex items-center gap-1 text-sm text-ink-muted transition-colors hover:text-ink-body"
        >
          <ArrowLeft className="h-4 w-4" />
          Kembali ke Data X-Ray
        </Link>
      </div>

      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="truncate">{title}</h1>
          <p className="mt-1 text-sm text-ink-muted">
            {run.file_count} file · {formatBytes(run.total_bytes)} · diupload oleh{" "}
            {run.uploaded_by ?? "—"} · {formatDate(run.created_at)}
            {run.generated_at && <> · digenerate {formatDate(run.generated_at)}</>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={run.status} />
          <Button
            variant="secondary"
            size="sm"
            className="gap-2"
            onClick={() =>
              router.post(`/admin/data-xray/${run.id}/generate`, {}, { preserveScroll: true })
            }
            disabled={run.status === "pending" || run.status === "processing"}
          >
            <RefreshCw className="h-4 w-4" />
            Regenerate X-Ray
          </Button>
        </div>
      </div>

      <ResultBadges run={run} />

      {run.error_message && (
        <Alert tone="danger" title="Generasi gagal" className="mt-4" icon={<XCircle className="h-4 w-4" />}>
          <p className="text-sm">{run.error_message}</p>
        </Alert>
      )}

      {note && (
        <Alert tone="info" title="X-Ray tidak digenerate" className="mt-4">
          <p className="text-sm">{note}</p>
        </Alert>
      )}

      {showUnmappedHint && (
        <Alert
          tone="warning"
          title="Sumber baru — belum dipetakan"
          className="mt-4"
          icon={<FilePlus2 className="h-4 w-4" />}
        >
          <p className="text-sm">
            X-ray <strong>struktural</strong> berhasil dibuat, tetapi konteks bisnis sumber ini masih
            membaca <em>(belum dipetakan)</em>. Untuk meng-onboard <code>{run.source_folder}</code> ke
            katalog, tambahkan entri <code>CTX["{run.source_folder}"]</code> di{" "}
            <code>.agent/skills/data-xray/matalangit_xray.py</code> (cat/title/table/units/purpose/quirks/bizlines/pii),
            lalu jalankan ulang generator. Langkah lengkap ada di bagian{" "}
            <strong>“Onboarding a NEW source into the catalog”</strong> pada{" "}
            <code>.agent/skills/data-xray/SKILL.md</code>.
          </p>
        </Alert>
      )}

      {/* Per-file statuses */}
      <section className="mt-8">
        <h2>File</h2>
        <FileTable files={files} />
      </section>

      {/* Generated _FOLDER_XRAY.md viewer + downloads */}
      <section className="mt-8">
        <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2>Dokumen X-Ray</h2>
            <p className="mt-1 text-sm text-ink-muted">
              Hanya meta yang dihasilkan generator — tidak pernah baris data mentah.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {leaves.length > 1 && (
              <div className="w-56">
                <label htmlFor="leaf-select" className="sr-only">
                  Pilih folder leaf
                </label>
                <Select
                  id="leaf-select"
                  value={selected_leaf ?? ""}
                  onChange={(e) => switchLeaf(e.target.value)}
                >
                  {leaves.map((l) => (
                    <option key={l.leaf} value={l.leaf}>
                      {l.label}
                    </option>
                  ))}
                </Select>
              </div>
            )}
            {selectedLeafEntry?.xray_md_available && (
              <Button asChild variant="secondary" size="sm" className="gap-2">
                <a href={downloadUrl(selectedLeafEntry.leaf, "xray_md")} download>
                  <Download className="h-4 w-4" />
                  _FOLDER_XRAY.md
                </a>
              </Button>
            )}
            {selectedLeafEntry?.extract_json_available && (
              <Button asChild variant="secondary" size="sm" className="gap-2">
                <a href={downloadUrl(selectedLeafEntry.leaf, "extract_json")} download>
                  <Download className="h-4 w-4" />
                  _extract.json
                </a>
              </Button>
            )}
          </div>
        </div>

        <MarkdownViewer markdown={xray_markdown} truncated={xray_markdown_truncated} />
      </section>
    </AppShell>
  )
}

// ---------------------------------------------------------------------------
// Markdown viewer (sanitized — XSS-safe even though sheet/column names that
// the generator embeds are not path-validated). marked + DOMPurify are both
// deterministic, so server and client render identical HTML (no hydration
// mismatch); isomorphic-dompurify sanitizes in Node + browser alike.
// ---------------------------------------------------------------------------

function MarkdownViewer({ markdown, truncated }: { markdown: string | null; truncated: boolean }) {
  const html = React.useMemo(() => {
    if (!markdown) return null
    const raw = marked.parse(markdown, { async: false }) as string
    return DOMPurify.sanitize(raw)
  }, [markdown])

  if (!html) {
    return (
      <div className="rounded-md border border-dashed border-edge bg-surface px-4 py-10 text-center">
        <FileText className="mx-auto h-6 w-6 text-ink-muted" />
        <p className="mt-2 text-sm text-ink-muted">
          Belum ada <code>_FOLDER_XRAY.md</code> untuk folder ini. Jalankan/Regenerate x-ray dulu.
        </p>
      </div>
    )
  }

  return (
    <div className="rounded-md border border-edge bg-surface">
      {truncated && (
        <Alert tone="warning" className="m-3">
          <p className="text-sm">
            Dokumen besar — pratinjau dipotong. Unduh <code>_FOLDER_XRAY.md</code> untuk versi lengkap.
          </p>
        </Alert>
      )}
      <article
        className="data-xray-md max-w-none overflow-x-auto px-5 py-4 [&_table]:block [&_table]:overflow-x-auto [&_table]:whitespace-nowrap [&_code]:break-words"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Per-file table
// ---------------------------------------------------------------------------

function FileTable({ files }: { files: XrayFile[] }) {
  if (files.length === 0) {
    return <p className="text-sm text-ink-muted">Tidak ada file pada run ini.</p>
  }

  return (
    <>
      {/* Desktop */}
      <div className="hidden overflow-x-auto rounded-md border border-edge md:block">
        <table className="w-full text-sm">
          <thead className="bg-surface">
            <tr className="text-left text-xs uppercase tracking-wide text-ink-muted">
              <th className="px-4 py-2 font-medium">File</th>
              <th className="px-4 py-2 text-right font-medium">Ukuran</th>
              <th className="px-4 py-2 font-medium">Template</th>
              <th className="px-4 py-2 font-medium">Fingerprint</th>
              <th className="px-4 py-2 font-medium">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-edge">
            {files.map((f) => (
              <tr key={f.id}>
                <td className="px-4 py-2 font-medium text-ink-display">
                  <span className="block truncate" title={f.relative_path}>
                    {f.relative_path}
                  </span>
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-ink-body">
                  {formatBytes(f.byte_size)}
                </td>
                <td className="px-4 py-2">
                  <TemplateBadge status={f.template_status} />
                </td>
                <td className="px-4 py-2">
                  <code className="text-xs text-ink-muted">{f.fingerprint ?? "—"}</code>
                </td>
                <td className="px-4 py-2">
                  <FileStatusBadges file={f} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile */}
      <div className="space-y-2 md:hidden">
        {files.map((f) => (
          <div key={f.id} className="rounded-md border border-edge bg-surface p-3">
            <p className="truncate font-medium text-ink-display" title={f.relative_path}>
              {f.relative_path}
            </p>
            <p className="mt-0.5 text-xs text-ink-muted">
              {formatBytes(f.byte_size)}
              {f.fingerprint && <> · {f.fingerprint}</>}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <TemplateBadge status={f.template_status} />
              <FileStatusBadges file={f} />
            </div>
          </div>
        ))}
      </div>
    </>
  )
}

function TemplateBadge({ status }: { status: string | null }) {
  if (!status) return <Badge tone="muted">—</Badge>
  switch (status) {
    case "NEW":
      return (
        <Badge tone="accent">
          <FilePlus2 className="mr-1 h-3 w-3" />
          template baru
        </Badge>
      )
    case "unreadable":
      return (
        <Badge tone="danger">
          <ShieldAlert className="mr-1 h-3 w-3" />
          tidak terbaca
        </Badge>
      )
    case "known":
      return <Badge tone="success">known</Badge>
    case "baseline":
      return <Badge tone="muted">baseline</Badge>
    default:
      return <Badge tone="muted">{status}</Badge>
  }
}

function FileStatusBadges({ file }: { file: XrayFile }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {file.integrity_error ? (
        <Badge tone="danger" title={file.integrity_error}>
          <ShieldAlert className="mr-1 h-3 w-3" />
          integritas
        </Badge>
      ) : file.ingest_ok ? (
        <Badge tone="success">
          <CheckCircle2 className="mr-1 h-3 w-3" />
          ingest_ok
        </Badge>
      ) : (
        <Badge tone="muted">—</Badge>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Shared badges (mirror Runs.tsx)
// ---------------------------------------------------------------------------

function ResultBadges({ run }: { run: RunDetail }) {
  if (run.status !== "completed") return null

  const hasMeta = run.meta_files_written != null
  const hasIngest = run.ingest_ok_count != null
  const hasMapped = run.source_mapped != null
  if (
    !hasMeta &&
    !hasIngest &&
    !hasMapped &&
    run.new_template_count === 0 &&
    run.integrity_issue_count === 0
  ) {
    return null
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {hasMeta && (
        <Badge tone="muted">
          <FileSpreadsheet className="mr-1 h-3 w-3" />
          {run.meta_files_written} meta
        </Badge>
      )}
      {run.new_template_count > 0 && (
        <Badge tone="accent">
          <FilePlus2 className="mr-1 h-3 w-3" />
          {run.new_template_count} template baru
        </Badge>
      )}
      {run.integrity_issue_count > 0 && (
        <Badge tone="danger">
          <ShieldAlert className="mr-1 h-3 w-3" />
          {run.integrity_issue_count} korup
        </Badge>
      )}
      {hasIngest && (
        <Badge tone="success">
          <CheckCircle2 className="mr-1 h-3 w-3" />
          {run.ingest_ok_count}/{run.file_count} ingest_ok
        </Badge>
      )}
      {hasMapped &&
        (run.source_mapped ? (
          <Badge tone="success">termapping</Badge>
        ) : (
          <Badge tone="muted">belum dipetakan</Badge>
        ))}
    </div>
  )
}

function StatusBadge({ status }: { status: RunStatus }) {
  switch (status) {
    case "pending":
      return (
        <Badge tone="muted">
          <Clock className="mr-1 h-3 w-3" />
          Pending
        </Badge>
      )
    case "processing":
      return (
        <Badge tone="accent">
          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
          Processing
        </Badge>
      )
    case "completed":
      return (
        <Badge tone="success">
          <CheckCircle2 className="mr-1 h-3 w-3" />
          Completed
        </Badge>
      )
    case "failed":
      return (
        <Badge tone="danger">
          <XCircle className="mr-1 h-3 w-3" />
          Failed
        </Badge>
      )
    case "cancelled":
      return <Badge tone="muted">Cancelled</Badge>
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("id-ID", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}
